const platforms = {
  WEBOS: "WebOS",
  CHROMEOS: "ChromeOS",
  LINUX: "Linux",
  WINDOWS: "Windows",
};

platforms.ALL = Object.values(platforms);
platforms.DEFAULT = [platforms.LINUX, platforms.WINDOWS];

const flags = {
  CHECK_PLAYER_SECONDS: {
    key: "--check-player-seconds",
    type: String,
    label: "Check Player Seconds",
    explanation: `The watchdog will check player health every X seconds.`,
    default: "240",
    flagType: "watchdog",
  },
  FOCUS_WINDOW_SECONDS: {
    key: "--focus-window-seconds",
    type: Number,
    label: "Focus Window Seconds",
    explanation: `The watchdog will try to focus the player window
      every X seconds.`,
    default: "5",
    flagType: "watchdog",
  },
  HIDE_CURSOR_SECONDS: {
    key: "--hide-cursor-seconds",
    type: Number,
    label: "Hide Cursor Seconds",
    explanation: `The watchdog will try to hide the cursor every X seconds.`,
    default: "120",
    flagType: "watchdog",
  },
  MAX_MEMORY_PER_APP: {
    key: "--max-memory-per-app",
    type: Number,
    label: "Max Memory Per App",
    explanation: `Sets the memory limit for each app process in bytes.
      Default is 400MBs.`,
    default: "419430400",
    flagType: "other",
  },
  LOCAL_SERVER_PORT: {
    key: "--local-server-port",
    type: Number,
    label: "Local Server Port",
    explanation: `Overrides local server port in version 2.57.0 and later.
      Default is 8000.`,
    flagType: "other",
    platforms: [platforms.LINUX, platforms.WINDOWS],
    validity: {
      startsIn: "2.57.0",
      endsIn: null,
    },
  },
  WINDOW_WIDTH: {
    key: "--window-width",
    type: Number,
    label: "Window Width",
    explanation: `Sets player window's width.
      Player runs in fullscreen by default.`,
    flagType: "window",
    platforms: [platforms.CHROMEOS, platforms.LINUX, platforms.WINDOWS],
  },
  WINDOW_HEIGHT: {
    key: "--window-height",
    type: Number,
    label: "Window Height",
    explanation: `Sets player window's height.
      Player runs in fullscreen by default.`,
    flagType: "window",
    platforms: [platforms.CHROMEOS, platforms.LINUX, platforms.WINDOWS],
  },
  WINDOW_X: {
    key: "--window-x",
    type: Number,
    label: "Window X",
    explanation: `Sets player window's x coordinate position.
      Player runs in fullscreen by default.`,
    flagType: "window",
  },
  WINDOW_Y: {
    key: "--window-y",
    type: Number,
    label: "Window Y",
    explanation: `Sets player window's y coordinate position.
      Player runs in fullscreen by default.`,
    flagType: "window",
  },
  DISABLE_WINDOW_RESIZE: {
    key: "--disable-window-resize",
    type: Boolean,
    label: "Disable Window Resize",
    explanation: `This flag disables resizing of the player window in version
      2.56.0 and later. This may make local debugging difficult.`,
    default: "false",
    flagType: "window",
  },
  DISABLE_ALERTS: {
    key: "--disable-alerts",
    type: Boolean,
    label: "Disable Alerts",
    explanation: `When this flag is set, the player will not send any alerts.
      (e.g. Device restarted)`,
    default: "false",
    flagType: "other",
  },
  DISABLE_TRANSITIONS: {
    key: "--disable-transitions",
    type: Boolean,
    label: "Disable Transitions",
    explanation: `When this flag is set, the player will disable
      all transitions between content changes.`,
    default: "false",
    flagType: "window",
    platforms: platforms.ALL,
  },
  DISABLE_WATCHDOG: {
    key: "--disable-watchdog",
    type: Boolean,
    label: "Disable Watchdog",
    explanation: `When this flag is set, the player will not
      start the watchdog process`,
    default: "false",
    flagType: "watchdog",
  },
  DISABLE_WINDOW_FOCUS: {
    key: "--disable-window-focus",
    type: Boolean,
    label: "Disable Window Focus",
    explanation: `When this flag is set, the watchdog will not try to
      focus the player window.`,
    default: "false",
    flagType: "watchdog",
  },
  DISABLE_AUTO_HIDE_TASKBAR: {
    key: "--disable-auto-hide-taskbar",
    type: Boolean,
    label: "Disable Auto-hide Taskbar",
    explanation: `When this flag is set, the watchdog will not try to
      hide the taskbar.`,
    default: "false",
    flagType: "watchdog",
    platforms: [platforms.WINDOWS],
  },
  DISABLE_WINDOW_ALWAYS_ON_TOP: {
    key: "--disable-window-always-on-top",
    type: Boolean,
    label: "Disable Window Always on Top",
    explanation: `When this flag is set, the watchdog will not set
      always-on-top flag for the player window.`,
    flagType: "watchdog",
  },
  ROTATE_SCREEN: {
    key: "--window-rotation",
    type: Array,
    label: "Rotate Screen",
    explanation: "Rotates the device screen clockwise by X degrees.",
    default: "0",
    flagType: "window",
    options: [
      {
        label: "0 degrees",
        value: "0",
      },
      {
        label: "90 degrees",
        value: "90",
      },
      {
        label: "180 degrees",
        value: "180",
      },
      {
        label: "270 degrees",
        value: "270",
      },
    ],
    platforms: platforms.ALL,
  },
  OVERRIDE_NETWORK_FLAGS: {
    key: "--override-network-flags",
    type: Boolean,
    label: "Override network flags with device flags.",
    explanation: `When checked, will use device flag settings instead of
      network flags.`,
    default: "false",
    flagType: "device",
    platforms: platforms.ALL,
  },
  HIDE_WINDOW_ON_STARTUP: {
    key: "--window-hide",
    type: Boolean,
    label: "Hide Window on Startup",
    explanation: "When checked, hides player window on device startup",
    default: "false",
    flagType: "window",
    platforms: platforms.DEFAULT,
  },
  USE_IN_MEMORY_STORAGE_PARTITION: {
    key: "--use-in-memory-storage-partition",
    type: Boolean,
    label: "Use In Memory Storage Partition",
    explanation: `When checked, the player will use an inmemory storage
      partition. Browsing data like cookies and cache will be cleared on
      reboots`,
    default: "false",
    flagType: "other",
    platforms: [platforms.CHROMEOS],
  },
  ENABLE_MULTIZONE: {
    key: "--enable-multizone",
    type: Boolean,
    label: "Enable Multizone",
    explanation:
      "When checked, the player will run with multizone support." +
      "Only works when disable applications flag is enabled.",
    default: "false",
    flagType: "other",
    platforms: [platforms.WEBOS, platforms.CHROMEOS],
  },
  ENABLE_AUDIO: {
    key: "--enable-audio",
    type: Boolean,
    label: "Enable Audio",
    explanation: "When checked, the player will play video with audio enabled.",
    default: "false",
    flagType: "other",
    platforms: platforms.ALL,
  },
  BIND_SERVER: {
    key: "--bind-server-to-all-interfaces",
    type: Boolean,
    label: "Bind Local Server to All Interfaces",
    explanation:
      "When checked, will run the local server on 0.0.0.0 instead" +
      " of localhost",
    default: "false",
    flagType: "other",
    platforms: [platforms.LINUX, platforms.WINDOWS],
  },
  LOG_RETENTION_DAYS: {
    key: "--log-retention-days",
    type: Number,
    label: "Player Log Lifespan",
    explanation: `Sets the TTL/retention time of player logs in days.`,
    default: "7",
    flagType: "other",
    platforms: [platforms.LINUX, platforms.WEBOS, platforms.WINDOWS],
    validity: {
      startsIn: "2.49.2",
      endsIn: null,
    },
  },
  TIMESERVER_URL: {
    key: "--timeserver-url",
    type: String,
    label: "Timeserver URL",
    explanation: `The time server URL your device clock is compared to
      determine its accuracy or drift. If you use VSA, set this to the
      time server you configured your device clock to sync with to make
      logs accurate. `,
    default: "time.windows.com",
    flagType: "other",
    platforms: [platforms.DEFAULT],
    validity: {
      startsIn: "3.0.2",
      endsIn: null,
    },
  },
  SNAPSHOT_ENABLED: {
    key: "--snapshot-enabled",
    type: Boolean,
    label: "Enable webcam snapshots ",
    explanation: `When enabled, the Player triggers an IP webcam to capture
      snapshots during ad playback (when using supported apps like VSA).`,
    default: "false",
    flagType: "webcam",
    platforms: [platforms.DEFAULT],
    validity: {
      startsIn: "4.0.0",
      endsIn: null,
    },
  },
  SNAPSHOT_FREQUENCY: {
    key: "--snapshot-frequency",
    type: Number,
    label: "Snapshot frequency (seconds)",
    explanation: `The minimum time between webcam snapshots of a single
      creative. For example, when set to 86400 (24 hours), the Player
      takes a snapshot of each creative once every 24 hours. To take
      snapshots of all ad plays, set this to 0.`,
    default: 86400,
    flagType: "webcam",
    platforms: [platforms.DEFAULT],
    validity: {
      startsIn: "4.0.0",
      endsIn: null,
    },
  },
  SNAPSHOT_WEBCAM_URL: {
    key: "--snapshot-webcam-url",
    type: String,
    label: "Webcam snapshot URL",
    explanation: `When webcam snapshots are enabled, the Player sends a GET
      request to this URL to capture a snapshot. You can include
      camera-specific parameters (e.g., compression, aspect ratio) in the
      URL. To auto-detect an ONVIF-compatible camera on the network instead,
      leave this blank and enable Auto-detect webcam.`,
    default: null,
    flagType: "webcam",
    platforms: [platforms.DEFAULT],
    validity: {
      startsIn: "4.0.0",
      endsIn: null,
    },
  },
  SNAPSHOT_LIFESPAN: {
    key: "--snapshot-lifespan",
    type: Number,
    label: "Local snapshot lifespan (hours)",
    explanation: `The amount of time the device stores webcam snapshots
      locally if it is unable to offload them to Vistar’s server. It
      automatically removes snapshots when it successfully offloads them.
      You’ll be notified by  events hourly until the snapshots are removed
      from the device.`,
    default: 48,
    flagType: "webcam",
    platforms: [platforms.DEFAULT],
    validity: {
      startsIn: "4.0.0",
      endsIn: null,
    },
  },
  SNAPSHOT_DELETION_WARNING: {
    key: "--snapshot-deletion-warning",
    type: Number,
    label: "Snapshot deletion warning (hours)",
    explanation: `How far in advance you want to be notified before webcam
      snapshots that haven’t been offloaded to Vistar’s server are deleted
      from the device.`,
    default: 24,
    flagType: "webcam",
    platforms: [platforms.DEFAULT],
    validity: {
      startsIn: "4.0.0",
      endsIn: null,
    },
  },
  SNAPSHOT_AUTO_DETECT: {
    key: "--snapshot-auto-detect",
    type: Boolean,
    label: "Auto-detect webcam",
    explanation: `When enabled, the Player scans its network for an
      ONVIF-compatible camera and uses it to take snapshots. Has no effect
      if a Webcam Snapshot URL is set.`,
    default: "false",
    flagType: "webcam",
    platforms: [platforms.DEFAULT],
    validity: {
      startsIn: "4.0.0",
      endsIn: null,
    },
  },
};

const flagsWithDefaults = Object.keys(flags).reduce((f, fKey) => {
  f[fKey] = Object.assign({ platforms: platforms.DEFAULT }, flags[fKey]);
  return f;
}, {});

const flagOptions = Object.values(flagsWithDefaults)
  .filter((flag) => flag.key !== flags.OVERRIDE_NETWORK_FLAGS.key)
  .map((flag) => {
    return {
      label: flag.label,
      value: flag.key,
    };
  });

export default flagsWithDefaults;
export { flagsWithDefaults as Flags, flagOptions };
